import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loading } from '@appkit4/react-components';

import { getPublicProject, PublicProjectResponse } from '@config/publicApis';
import { DepsMap, DesignSystems } from '@utils/index';

import styles from './PublicProject.module.scss';
import PublicSandpackRenderer from './PublicSandpackRenderer';

type LoadingState = 'loading' | 'success' | 'error';

interface ProjectData {
    files: Record<string, { code: string }>;
    designSystem: DesignSystems;
    templateVersion: string;
    chatSessionId: number;
    commitId: string;
}

const PublicProject = () => {
    const [searchParams] = useSearchParams();
    const publicId = searchParams.get('publicId') || '';
    const token = searchParams.get('token') || '';

    const [loadingState, setLoadingState] = useState<LoadingState>('loading');
    const [projectData, setProjectData] = useState<ProjectData | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const fetchProject = async () => {
            if (!publicId || !token) {
                setLoadingState('error');
                setErrorMessage('Invalid share link. Missing required parameters.');
                return;
            }

            try {
                setLoadingState('loading');
                const response: PublicProjectResponse = await getPublicProject(publicId, token);

                if (response.status === 'SUCCESS' && response.resultObject) {
                    const { files, designSystem, chatSessionId, commitId, templateVersion } = response.resultObject;

                    setProjectData({
                        files,
                        designSystem: designSystem as DesignSystems,
                        templateVersion: templateVersion || 'v2',
                        chatSessionId,
                        commitId,
                    });
                    setLoadingState('success');
                } else {
                    setLoadingState('error');
                    setErrorMessage(response.message || 'Failed to load project.');
                }
            } catch (error: any) {
                console.error('Error loading public project:', error);
                setLoadingState('error');

                if (error.response?.status === 401 || error.response?.status === 403) {
                    setErrorMessage('This link has expired or is no longer valid.');
                } else if (error.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('Unable to load the shared project. Please try again later.');
                }
            }
        };

        fetchProject();
    }, [publicId, token]);

    const dependencies = useMemo(() => {
        if (!projectData?.designSystem) return DepsMap['Appkit'];
        return DepsMap[projectData.designSystem] || DepsMap['Appkit'];
    }, [projectData?.designSystem]);

    if (loadingState === 'loading') {
        return (
            <div className={styles['public-project']}>
                <div className={styles['loading-container']}>
                    <Loading loadingType="circular" indeterminate />
                    <p className={styles['loading-text']}>Loading shared project...</p>
                </div>
            </div>
        );
    }

    if (loadingState === 'error') {
        return (
            <div className={styles['public-project']}>
                <div className={styles['error-container']}>
                    <span className="Appkit4-icon icon-alert-outline ap-font-32" />
                    <h2>Unable to Load Project</h2>
                    <p>{errorMessage}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles['public-project']}>
            {projectData && (
                <PublicSandpackRenderer
                    files={projectData.files}
                    dependencies={dependencies}
                    templateVersion={projectData.templateVersion}
                    designSystem={projectData.designSystem}
                />
            )}
        </div>
    );
};

export default PublicProject;
